import React, { useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useDrilldownMetaCompra } from '../../hooks/useMetaCompra';
import { money } from '../../utils/format';
import type { DimensaoDrilldown } from '../../types/metaCompra';

interface Props {
  anoMes: string;
  empresas: string[];
  dimensao: DimensaoDrilldown;
  codigo: string;
  /** Título amigável da dimensão clicada (ex: nome do fornecedor). */
  titulo: string;
  onClose: () => void;
}

const fmtData = (iso?: string | null) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return '—'; }
};

const labelDimensao = (d: DimensaoDrilldown): string =>
  d === 'fornecedor' ? 'Fornecedor' : d === 'marca' ? 'Marca' : 'Grupo';

const MetaCompraDrillDownDrawer: React.FC<Props> = ({
  anoMes, empresas, dimensao, codigo, titulo, onClose,
}) => {
  const { data: pedidos = [], isLoading, error } = useDrilldownMetaCompra(
    anoMes, empresas, dimensao, codigo, true,
  );

  // Fecha com Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const totalPedido = pedidos.reduce((acc, p) => acc + p.valorPedido, 0);
  const totalCancelado = pedidos.reduce((acc, p) => acc + p.valorCancelado, 0);
  const totalSaldo = pedidos.reduce((acc, p) => acc + p.valorAtual, 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-drill-titulo"
      className="fixed inset-0 z-40 flex"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex-1 bg-[var(--scrim)] backdrop-blur-[2px]" />
      <div className="flex h-full w-full max-w-2xl flex-col border-l border-line-strong bg-surface shadow-iris-xl">
        {/* Header */}
        <div className="flex items-start gap-2 border-b border-line px-4 py-3">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wide text-tx-3">{labelDimensao(dimensao)}</p>
            <h2 id="drawer-drill-titulo" className="text-sm font-semibold text-tx">{titulo}</h2>
            <p className="mt-0.5 text-xs text-tx-2">
              {anoMes} · {empresas.length} empresa(s) · {pedidos.length} pedidos
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-tx-3 hover:bg-surface-2"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Totais agregados — Pedido / Cancelado / Saldo */}
        {pedidos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 border-b border-line bg-surface-2 px-4 py-2 text-xs">
            <div>
              <div className="text-[10px] uppercase text-tx-3">Pedido</div>
              <div className="text-tx-2">{money(totalPedido)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-tx-3">Cancelado</div>
              <div className={`font-medium ${totalCancelado > 0 ? 'text-danger' : 'text-tx-3'}`}>
                {totalCancelado > 0 ? `−${money(totalCancelado)}` : money(0)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-tx-3">Saldo</div>
              <div className="font-semibold text-tx">{money(totalSaldo)}</div>
            </div>
          </div>
        )}

        {/* Lista de pedidos */}
        <div className="flex-1 overflow-auto">
          {isLoading ? (
            <p className="p-6 text-center text-sm text-tx-3">Carregando pedidos…</p>
          ) : error ? (
            <p className="p-6 text-center text-sm text-danger">Erro ao carregar pedidos.</p>
          ) : pedidos.length === 0 ? (
            <p className="p-6 text-center text-sm text-tx-3">Nenhum pedido neste filtro.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-surface-2">
                <tr className="border-b border-line text-left text-tx-2">
                  <th className="px-3 py-2 font-medium">Pedido</th>
                  <th className="px-3 py-2 font-medium">Data</th>
                  <th className="px-3 py-2 font-medium">Fornecedor</th>
                  <th className="px-3 py-2 text-right font-medium">Pedido</th>
                  <th className="px-3 py-2 text-right font-medium">Cancelado</th>
                  <th className="px-3 py-2 text-right font-medium">Saldo</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((p) => (
                  <tr
                    key={`${p.empresa}|${p.codigo}`}
                    className="border-b border-line last:border-0 hover:bg-surface-2"
                  >
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-tx-2">
                      <span className="text-tx-3">{p.empresa}/</span>{p.codigo}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-tx-2">{fmtData(p.data)}</td>
                    <td className="px-3 py-2 text-tx-2">
                      <span className="font-mono text-[10px] text-tx-3">{p.fornecedor ?? '—'}</span>
                      {p.nomeFornecedor && <div className="text-[11px]">{p.nomeFornecedor}</div>}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right text-tx-2">
                      {money(p.valorPedido)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right">
                      {p.temCancelamentoTotal ? (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-danger-soft px-2 py-0.5 text-[10px] font-medium text-danger">
                          <ExclamationTriangleIcon className="h-2.5 w-2.5" />
                          −{money(p.valorCancelado)}
                        </span>
                      ) : p.temCancelamentoParcial ? (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-medium text-warning">
                          <ExclamationTriangleIcon className="h-2.5 w-2.5" />
                          −{money(p.valorCancelado)}
                        </span>
                      ) : (
                        <span className="text-tx-3">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-tx">
                      {money(p.valorAtual)}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-medium text-tx-2">
                        {p.status ?? '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaCompraDrillDownDrawer;
